'use client';

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';

interface LoadingState {
  operations: Map<
    string,
    {
      isLoading: boolean;
      startTime: number;
      message?: string;
      progress?: number;
    }
  >;
}

interface LoadingActions {
  startLoading: (operationId: string, message?: string) => void;
  stopLoading: (operationId: string) => void;
  updateProgress: (operationId: string, progress: number) => void;
  updateMessage: (operationId: string, message: string) => void;
  isOperationLoading: (operationId: string) => boolean;
  clearAll: () => void;
  getOperationDuration: (operationId: string) => number | null;
}

type LoadingStore = LoadingState & LoadingActions;

const initialState: LoadingState = {
  operations: new Map(),
};

export const useLoadingStore = create<LoadingStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,

      startLoading: (operationId: string, message?: string) => {
        set((state) => {
          state.operations.set(operationId, {
            isLoading: true,
            startTime: Date.now(),
            message,
            progress: 0,
          });
        });
      },

      stopLoading: (operationId: string) => {
        set((state) => {
          state.operations.delete(operationId);
        });
      },

      updateProgress: (operationId: string, progress: number) => {
        set((state) => {
          const operation = state.operations.get(operationId);
          if (operation) {
            operation.progress = Math.min(100, Math.max(0, progress));
          }
        });
      },

      updateMessage: (operationId: string, message: string) => {
        set((state) => {
          const operation = state.operations.get(operationId);
          if (operation) {
            operation.message = message;
          }
        });
      },

      isOperationLoading: (operationId: string) => {
        const state = get();
        return state.operations.has(operationId);
      },

      clearAll: () => {
        set((state) => {
          state.operations.clear();
        });
      },

      getOperationDuration: (operationId: string) => {
        const state = get();
        const operation = state.operations.get(operationId);
        if (!operation) return null;
        return Date.now() - operation.startTime;
      },
    }))
  )
);
